import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { AppDialog, type AppDialogAction } from './AppDialog'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { Pressable } from './Pressable'
import { MiniLoader } from './MiniLoader'
import { WorkspaceItemPicker } from './WorkspaceItemPicker'
import { getNlsString } from '../../nls/nlsRuntime'
import { uploadWorkspaceFiles } from '../../services/storageHubService'
import {
  createStockSupplier,
  subscribeStockSuppliers,
  SUPPLIER_FORM_SECTIONS,
} from '../../services/stockSupplierService'
import {
  createBudgetVendor,
  subscribeBudgetVendors,
  VENDOR_FORM_SECTIONS,
  type BudgetVendor,
} from '../../services/budgetVendorService'
import { subscribeTimelineElements } from '../../services/timelineElementSubscriptionService'
import { buildBudgetEntityChain } from '../../services/budgetEntityChain'
import type { StockSupplier } from '../../types/stock'
import type { TimelineElement } from '../../types/timelineElement'
import './entityFormDialog.css'

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'currency'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'switch'
  | 'image'
  | 'tagInput'
  | 'supplierPicker'
  | 'vendorPicker'
  | 'entityPicker'
  | 'memberPicker'
  | 'customKpiRepeater'
  | 'rewardTierRepeater'
  | 'simulatorStrip'

export interface SelectOption {
  label: string
  value: string | number
  color?: string
}

export interface FieldDef {
  id: string
  label: string
  type: FieldType
  required?: boolean
  placeholder?: string
  hint?: string
  validate?: (value: unknown, values: Record<string, unknown>) => string | null
  options?: Array<SelectOption> | string[]
  creatable?: boolean
  min?: number
  max?: number
  step?: number
  prefix?: string
  suffix?: string
  disabled?: boolean
  minorUnits?: boolean
  defaultValue?: unknown
  fullWidth?: boolean
}

export interface FieldSection {
  id: string
  label: string
  hint?: string
  fields: FieldDef[]
}

export interface EntityFormDialogProps {
  open: boolean
  entityKind: string
  targetId?: string | 'new' | null
  entityName: string
  entityTitle?: string
  sections: FieldSection[]
  initialValues?: Record<string, unknown>
  workspaceId?: string
  currency?: string
  members?: Array<{ id: string; displayName: string; email?: string; role?: string }>
  elements?: TimelineElement[]
  onClose: () => void
  onSave: (values: Record<string, unknown>) => Promise<void> | void
  onSaveAndAddAnother?: (values: Record<string, unknown>) => Promise<void> | void
  validateAll?: (values: Record<string, unknown>) => Record<string, string>
  onFieldAction?: (fieldId: string, actionData: { action: string; target: string; amount: number } | Record<string, unknown>) => void
  onFieldChange?: (
    fieldId: string,
    value: unknown,
    setFieldValue: (targetFieldId: string, val: unknown) => void,
  ) => void
}

const buildDefaultValues = (sections: FieldSection[]): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {}
  for (const section of sections) {
    for (const field of section.fields) {
      if (field.defaultValue !== undefined) {
        defaults[field.id] = field.defaultValue
      } else if (field.type === 'switch') {
        defaults[field.id] = false
      } else if (field.type === 'tagInput' || field.type === 'multiselect') {
        defaults[field.id] = []
      } else if (field.type === 'number' || field.type === 'currency') {
        defaults[field.id] = ''
      } else {
        defaults[field.id] = ''
      }
    }
  }
  return defaults
}

interface CurrencyFieldInputProps {
  inputId: string
  field: FieldDef
  currency: string
  rawVal: unknown
  error?: string
  errorId: string
  hintId: string
  updateValue: (fieldId: string, val: unknown) => void
}

const CurrencyFieldInput = ({
  inputId,
  field,
  currency,
  rawVal,
  error,
  errorId,
  hintId,
  updateValue,
}: CurrencyFieldInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [draft, setDraft] = useState('')

  const formattedVal = useMemo(() => {
    if (field.minorUnits && typeof rawVal === 'number') {
      return (rawVal / 100).toFixed(2)
    }
    return rawVal != null ? String(rawVal) : ''
  }, [field.minorUnits, rawVal])

  const displayVal = isFocused ? draft : formattedVal

  return (
    <div className="entityFormDialog__currencyGroup">
      <span className="entityFormDialog__currencyPrefix">{field.prefix ?? currency}</span>
      <input
        id={inputId}
        type="number"
        step="0.01"
        min={field.min ?? 0}
        className="entityFormDialog__input"
        placeholder={field.placeholder ?? '0.00'}
        value={displayVal}
        disabled={field.disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : field.hint ? hintId : undefined}
        onFocus={() => {
          setIsFocused(true)
          setDraft(formattedVal)
        }}
        onChange={(e) => {
          const text = e.target.value
          setIsFocused(true)
          setDraft(text)
          if (text === '') {
            updateValue(field.id, null)
          } else {
            const num = parseFloat(text)
            if (!isNaN(num)) {
              updateValue(field.id, field.minorUnits ? Math.round(num * 100) : num)
            }
          }
        }}
        onBlur={() => {
          setIsFocused(false)
          if (draft === '') {
            updateValue(field.id, null)
          } else {
            const num = parseFloat(draft)
            if (!isNaN(num)) {
              updateValue(field.id, field.minorUnits ? Math.round(num * 100) : num)
            }
          }
        }}
      />
    </div>
  )
}

export const EntityFormDialog = ({
  open,
  entityKind,
  targetId = 'new',
  entityName,
  entityTitle,
  sections,
  initialValues,
  workspaceId,
  currency = 'USD',
  members,
  elements,
  onClose,
  onSave,
  onSaveAndAddAnother,
  validateAll,
  onFieldAction,
  onFieldChange,
}: EntityFormDialogProps) => {
  const isEditing = targetId !== 'new' && Boolean(targetId)
  const storageKey = `entityFormDialog:${entityKind}:${targetId || 'new'}`
  const formId = useId()
  const containerRef = useRef<HTMLDivElement>(null)

  const defaultValues = useMemo(() => buildDefaultValues(sections), [sections])

  const [values, setValues] = useState<Record<string, unknown>>(() => {
    try {
      const saved = sessionStorage.getItem(storageKey)
      if (saved) {
        return { ...defaultValues, ...(initialValues ?? {}), ...JSON.parse(saved) }
      }
    } catch {
      // ignore JSON parse errors from sessionStorage
    }
    return { ...defaultValues, ...(initialValues ?? {}) }
  })

  const [isDirty, setIsDirty] = useState<boolean>(() => {
    try {
      return Boolean(sessionStorage.getItem(storageKey))
    } catch {
      return false
    }
  })
  const [submitting, setSubmitting] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false)
  const [activeSupplierFieldId, setActiveSupplierFieldId] = useState<string | null>(null)
  const [suppliers, setSuppliers] = useState<StockSupplier[]>([])
  const [uploadingFieldId, setUploadingFieldId] = useState<string | null>(null)
  const [showAddVendorModal, setShowAddVendorModal] = useState(false)
  const [activeVendorFieldId, setActiveVendorFieldId] = useState<string | null>(null)
  const [vendors, setVendors] = useState<BudgetVendor[]>([])
  const [showEntityPickerModal, setShowEntityPickerModal] = useState(false)
  const [showMemberPickerModal, setShowMemberPickerModal] = useState(false)
  const [activeMemberFieldId, setActiveMemberFieldId] = useState<string | null>(null)
  const [activeEntityFieldId, setActiveEntityFieldId] = useState<string | null>(null)

  // Subscribe to vendors if workspaceId is provided
  useEffect(() => {
    if (!open || !workspaceId) return
    const hasVendorPicker = sections.some((s) => s.fields.some((f) => f.type === 'vendorPicker'))
    if (!hasVendorPicker) return

    const unsub = subscribeBudgetVendors(workspaceId, (list) => {
      setVendors(list)
    })
    return unsub
  }, [open, workspaceId, sections])


  const [timelineElements, setTimelineElements] = useState<TimelineElement[]>([])

  // Subscribe to timeline elements if workspaceId is provided and entityPicker exists
  useEffect(() => {
    if (!open || !workspaceId) return
    const hasEntityPicker = sections.some((s) => s.fields.some((f) => f.type === 'entityPicker'))
    if (!hasEntityPicker) return

    const unsub = subscribeTimelineElements(workspaceId, (list) => {
      setTimelineElements(list)
    })
    return unsub
  }, [open, workspaceId, sections])

  // Subscribe to suppliers if workspaceId is provided
  useEffect(() => {
    if (!open || !workspaceId) return
    const hasSupplierPicker = sections.some((s) => s.fields.some((f) => f.type === 'supplierPicker'))
    if (!hasSupplierPicker) return

    const unsub = subscribeStockSuppliers(workspaceId, (list) => {
      setSuppliers(list)
    })
    return unsub
  }, [open, workspaceId, sections])

  // Save to sessionStorage when values change
  const setFieldValue = useCallback((targetFieldId: string, val: unknown) => {
    setValues((prev) => {
      const next = { ...prev, [targetFieldId]: val }
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // ignore storage quota errors
      }
      return next
    })
    setIsDirty(true)
  }, [storageKey])

  const updateValue = useCallback((fieldId: string, val: unknown) => {
    setValues((prev) => {
      const next = { ...prev, [fieldId]: val }
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // ignore storage quota errors
      }
      return next
    })
    setIsDirty(true)
    onFieldChange?.(fieldId, val, setFieldValue)
  }, [storageKey, onFieldChange, setFieldValue])

  // Validation computation
  const errors = useMemo(() => {
    const errs: Record<string, string> = {}
    for (const section of sections) {
      for (const field of section.fields) {
        const val = values[field.id]
        if (field.required) {
          const isEmptyString = typeof val === 'string' && !val.trim()
          const isEmptyArray = Array.isArray(val) && val.length === 0
          const isNullOrUndefined = val === null || val === undefined || val === ''
          if (isEmptyString || isEmptyArray || isNullOrUndefined) {
            errs[field.id] = `${field.label} is required`
            continue
          }
        }
        if (field.validate) {
          const customError = field.validate(val, values)
          if (customError) {
            errs[field.id] = customError
          }
        }
      }
    }

    if (validateAll) {
      const formLevelErrors = validateAll(values)
      Object.assign(errs, formLevelErrors)
    }

    return errs
  }, [sections, values, validateAll])

  const hasErrors = Object.keys(errors).length > 0

  // Focus trap
  useEffect(() => {
    if (!open) return
    const container = containerRef.current
    if (!container) return

    const focusables = container.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length > 0) {
      focusables[0].focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const currentFocusables = container.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      )
      if (currentFocusables.length === 0) return

      const first = currentFocusables[0]
      const last = currentFocusables[currentFocusables.length - 1]

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  // Dismiss / Dirty Guard logic
  const handleDismiss = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true)
    } else {
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        // ignore
      }
      onClose()
    }
  }, [isDirty, storageKey, onClose])

  // Save handler
  const handleSave = useCallback(async () => {
    if (hasErrors || submitting) return
    setSubmitting(true)
    try {
      await onSave(values)
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        // ignore
      }
      setIsDirty(false)
      onClose()
    } catch (err) {
      console.warn('[EntityFormDialog] save failed', err)
    } finally {
      setSubmitting(false)
    }
  }, [hasErrors, submitting, onSave, values, storageKey, onClose])

  // Save & Add Another handler
  const handleSaveAndAddAnother = useCallback(async () => {
    if (hasErrors || submitting || !onSaveAndAddAnother) return
    setSubmitting(true)
    try {
      await onSaveAndAddAnother(values)
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        // ignore
      }
      setValues(defaultValues)
      setIsDirty(false)
    } catch (err) {
      console.warn('[EntityFormDialog] save & add another failed', err)
    } finally {
      setSubmitting(false)
    }
  }, [hasErrors, submitting, onSaveAndAddAnother, values, storageKey, defaultValues])

  // Global keydown: Cmd/Ctrl+Enter = Save, Enter on input submits
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        void handleSave()
        return
      }

      if (event.key === 'Enter') {
        const target = event.target as HTMLElement | null
        if (target && target.tagName === 'INPUT' && !target.classList.contains('entityFormDialog__tagInput')) {
          event.preventDefault()
          void handleSave()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, handleSave])

  // File upload for image field
  const handleImageUpload = async (fieldId: string, file: File) => {
    if (!workspaceId) return
    setUploadingFieldId(fieldId)
    try {
      const results = await uploadWorkspaceFiles({
        workspaceId,
        files: [file],
        visibility: 'WORKSPACE_SHARED',
        source: 'STOCK_IMAGE',
      })
      if (results[0]?.downloadUrl) {
        updateValue(fieldId, results[0].downloadUrl)
      }
    } catch (err) {
      console.warn('[EntityFormDialog] Image upload failed', err)
    } finally {
      setUploadingFieldId(null)
    }
  }

  // Render individual field
  const renderField = (field: FieldDef) => {
    const rawVal = values[field.id]
    const error = errors[field.id]
    const inputId = `${formId}-${field.id}`
    const errorId = `${inputId}-error`
    const hintId = `${inputId}-hint`

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            id={inputId}
            className="entityFormDialog__textarea"
            rows={3}
            placeholder={field.placeholder}
            value={typeof rawVal === 'string' ? rawVal : ''}
            disabled={field.disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : field.hint ? hintId : undefined}
            onChange={(e) => updateValue(field.id, e.target.value)}
          />
        )

      case 'number':
        return (
          <input
            id={inputId}
            type="number"
            className="entityFormDialog__input"
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            placeholder={field.placeholder}
            value={rawVal != null ? String(rawVal) : ''}
            disabled={field.disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : field.hint ? hintId : undefined}
            onChange={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value)
              updateValue(field.id, val)
            }}
          />
        )

      case 'currency':
        return (
          <CurrencyFieldInput
            inputId={inputId}
            field={field}
            currency={currency}
            rawVal={rawVal}
            error={error}
            errorId={errorId}
            hintId={hintId}
            updateValue={updateValue}
          />
        )


      case 'select': {
        const optionsList = Array.isArray(field.options)
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt,
            )
          : []

        if (field.creatable) {
          const listId = `${inputId}-options`
          return (
            <>
              <input
                id={inputId}
                type="text"
                list={listId}
                className="entityFormDialog__input"
                placeholder={field.placeholder ?? 'Select or type custom...'}
                value={typeof rawVal === 'string' ? rawVal : ''}
                disabled={field.disabled}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : field.hint ? hintId : undefined}
                onChange={(e) => updateValue(field.id, e.target.value)}
              />
              <datalist id={listId}>
                {optionsList.map((opt) => (
                  <option key={String(opt.value)} value={String(opt.value)}>
                    {opt.label}
                  </option>
                ))}
              </datalist>
            </>
          )
        }

        return (
          <select
            id={inputId}
            className="entityFormDialog__select"
            value={typeof rawVal === 'string' || typeof rawVal === 'number' ? rawVal : ''}
            disabled={field.disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : field.hint ? hintId : undefined}
            onChange={(e) => updateValue(field.id, e.target.value)}
          >
            {field.placeholder && <option value="">{field.placeholder}</option>}
            {optionsList.map((opt) => (
              <option key={String(opt.value)} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )
      }

      case 'multiselect': {
        const selected = Array.isArray(rawVal) ? (rawVal as string[]) : []
        const optionsList = Array.isArray(field.options)
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt,
            )
          : []

        return (
          <div className="entityFormDialog__tagContainer">
            {selected.map((item) => (
              <span key={item} className="entityFormDialog__tagChip">
                {optionsList.find((o) => o.value === item)?.label ?? item}
                <Pressable
                  className="entityFormDialog__tagRemoveBtn"
                  onClick={() => updateValue(field.id, selected.filter((s) => s !== item))}
                  aria-label={`Remove ${item}`}
                >
                  &times;
                </Pressable>
              </span>
            ))}
            <select
              className="entityFormDialog__select"
              style={{ width: 'auto', flex: 1, minWidth: '120px', border: 'none' }}
              value=""
              onChange={(e) => {
                const next = e.target.value
                if (next && !selected.includes(next)) {
                  updateValue(field.id, [...selected, next])
                }
              }}
            >
              <option value="">{field.placeholder ?? '+ Add item...'}</option>
              {optionsList
                .filter((opt) => !selected.includes(String(opt.value)))
                .map((opt) => (
                  <option key={String(opt.value)} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
            </select>
          </div>
        )
      }

      case 'date': {
        let dateString = ''
        if (typeof rawVal === 'string') {
          dateString = rawVal
        } else if (rawVal && typeof (rawVal as { toDate?: () => Date }).toDate === 'function') {
          dateString = (rawVal as { toDate: () => Date }).toDate().toISOString().split('T')[0]
        } else if (rawVal instanceof Date) {
          dateString = rawVal.toISOString().split('T')[0]
        }

        return (
          <input
            id={inputId}
            type="date"
            className="entityFormDialog__input"
            value={dateString}
            disabled={field.disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : field.hint ? hintId : undefined}
            onChange={(e) => updateValue(field.id, e.target.value || null)}
          />
        )
      }

      case 'switch': {
        const isChecked = Boolean(rawVal)
        return (
          <label className="entityFormDialog__switchRow">
            <div className={`entityFormDialog__switchTrack${isChecked ? ' is-checked' : ''}`}>
              <div className="entityFormDialog__switchThumb" />
            </div>
            <input
              id={inputId}
              type="checkbox"
              className="entityFormDialog__switchInput"
              checked={isChecked}
              disabled={field.disabled}
              aria-label={field.label}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : field.hint ? hintId : undefined}
              onChange={(e) => updateValue(field.id, e.target.checked)}
            />
            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {field.placeholder || field.label}
            </span>
          </label>
        )
      }

      case 'tagInput': {
        const tags = Array.isArray(rawVal) ? (rawVal as string[]) : []
        return (
          <div className="entityFormDialog__tagContainer">
            {tags.map((tag, idx) => (
              <span key={`${tag}-${idx}`} className="entityFormDialog__tagChip">
                {tag}
                <Pressable
                  className="entityFormDialog__tagRemoveBtn"
                  onClick={() => updateValue(field.id, tags.filter((_, i) => i !== idx))}
                  aria-label={`Remove tag ${tag}`}
                >
                  &times;
                </Pressable>
              </span>
            ))}
            <input
              type="text"
              className="entityFormDialog__tagInput"
              placeholder={tags.length === 0 ? field.placeholder ?? 'Type and press Enter...' : ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault()
                  e.stopPropagation()
                  const input = e.currentTarget
                  const text = input.value.trim()
                  if (text && !tags.includes(text)) {
                    updateValue(field.id, [...tags, text])
                    input.value = ''
                  }
                } else if (e.key === 'Backspace' && e.currentTarget.value === '' && tags.length > 0) {
                  updateValue(field.id, tags.slice(0, -1))
                }
              }}
            />
          </div>
        )
      }

      case 'image': {
        const imgUrl = typeof rawVal === 'string' ? rawVal : ''
        const isUploading = uploadingFieldId === field.id
        return (
          <div className="entityFormDialog__imageSection">
            {imgUrl ? (
              <img src={imgUrl} alt="Stock item" className="entityFormDialog__imagePreview" />
            ) : null}
            <div className="entityFormDialog__imageControls">
              <label className="entityFormDialog__uploadBtn">
                {isUploading ? <MiniLoader size="xs" inline /> : <CloudUploadRoundedIcon fontSize="small" />}
                <span>{isUploading ? 'Uploading...' : imgUrl ? 'Change image' : 'Upload photo'}</span>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void handleImageUpload(field.id, file)
                  }}
                />
              </label>
              {imgUrl && (
                <Button
                  variant="tertiary"
                  size="sm"
                  tone="danger"
                  onClick={() => updateValue(field.id, null)}
                >
                  Remove photo
                </Button>
              )}
            </div>
          </div>
        )
      }

      case 'supplierPicker': {
        const optionsList = Array.isArray(field.options) && field.options.length > 0
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt,
            )
          : suppliers.map((s) => ({ label: s.name, value: s.id }))

        return (
          <div className="entityFormDialog__pickerRow">
            <select
              id={inputId}
              className="entityFormDialog__select"
              value={typeof rawVal === 'string' ? rawVal : ''}
              disabled={field.disabled}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : field.hint ? hintId : undefined}
              onChange={(e) => updateValue(field.id, e.target.value || null)}
            >
              <option value="">{field.placeholder ?? 'Select supplier...'}</option>
              {optionsList.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {workspaceId && (
              <Button
                variant="secondary"
                size="md"
                icon={<AddRoundedIcon fontSize="small" />}
                onClick={() => {
                  setActiveSupplierFieldId(field.id)
                  setShowAddSupplierModal(true)
                }}
              >
                Add supplier
              </Button>
            )}
          </div>
        )
      }
      case 'vendorPicker': {
        const optionsList = Array.isArray(field.options) && field.options.length > 0
          ? field.options.map((opt) =>
              typeof opt === 'string' ? { label: opt, value: opt } : opt,
            )
          : vendors.map((v) => ({ label: v.name, value: v.id }))

        return (
          <div className="entityFormDialog__pickerRow">
            <select
              id={inputId}
              className="entityFormDialog__select"
              value={typeof rawVal === 'string' ? rawVal : ''}
              disabled={field.disabled}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : field.hint ? hintId : undefined}
              onChange={(e) => updateValue(field.id, e.target.value || null)}
            >
              <option value="">{field.placeholder ?? 'Select vendor...'}</option>
              {optionsList.map((opt) => (
                <option key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {workspaceId && (
              <Button
                variant="secondary"
                size="md"
                icon={<AddRoundedIcon fontSize="small" />}
                onClick={() => {
                  setActiveVendorFieldId(field.id)
                  setShowAddVendorModal(true)
                }}
              >
                Add vendor
              </Button>
            )}
          </div>
        )
      }

      case 'entityPicker': {
        const entityVal = rawVal as { id?: string; titles?: string[]; chain?: unknown[] } | null
        const displayTitle = entityVal?.titles?.[0] || (entityVal?.id ? `Target: ${entityVal.id}` : field.placeholder || 'Select target item...')
        return (
          <div className="entityFormDialog__pickerRow">
            <span className="entityFormDialog__input" style={{ display: 'flex', alignItems: 'center', flex: 1, background: 'var(--surface-elevated, inherit)' }}>
              {displayTitle}
            </span>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setActiveEntityFieldId(field.id)
                setShowEntityPickerModal(true)
              }}
            >
              Select target
            </Button>
          </div>
        )
      }

      case 'memberPicker': {
        const val = rawVal as string | string[]
        const memberIds = Array.isArray(val) ? val : (val ? [val] : [])
        const displays = memberIds.map(id => members?.find(m => m.id === id)?.displayName || id)
        const displayTitle = displays.length > 0 ? displays.join(', ') : (field.placeholder || 'Select member...')
        return (
          <div className="entityFormDialog__pickerRow">
            <span className="entityFormDialog__input" style={{ display: 'flex', alignItems: 'center', flex: 1, background: 'var(--surface-elevated, inherit)' }}>
              {displayTitle}
            </span>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                setActiveMemberFieldId(field.id)
                setShowMemberPickerModal(true)
              }}
            >
              {field.placeholder || 'Select'}
            </Button>
          </div>
        )
      }

      case 'customKpiRepeater': {
        const kpis = (rawVal as Array<{ label: string; targetValue: number; unit?: string }>) || []
        return (
          <div className="entityFormDialog__repeater">
            {kpis.map((kpi, idx) => (
              <div key={idx} className="entityFormDialog__repeaterRow" style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  placeholder="Label (e.g. Client meetings)"
                  value={kpi.label}
                  onChange={(e) => {
                    const next = [...kpis]
                    next[idx].label = e.target.value
                    updateValue(field.id, next)
                  }}
                  style={{ flex: 1 }}
                />
                <input
                  type="number"
                  placeholder="Target"
                  value={kpi.targetValue || ''}
                  onChange={(e) => {
                    const next = [...kpis]
                    next[idx].targetValue = Number(e.target.value)
                    updateValue(field.id, next)
                  }}
                  style={{ width: 80 }}
                />
                <input
                  type="text"
                  placeholder="Unit (opt)"
                  value={kpi.unit || ''}
                  onChange={(e) => {
                    const next = [...kpis]
                    next[idx].unit = e.target.value
                    updateValue(field.id, next)
                  }}
                  style={{ width: 80 }}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tone="danger"
                  label={getNlsString('NLSCommonEntityFormRemoveKpi')}
                  icon={<CloseRoundedIcon fontSize="small" />}
                  onClick={() => updateValue(field.id, kpis.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              icon={<AddRoundedIcon fontSize="small" />}
              onClick={() => updateValue(field.id, [...kpis, { label: '', targetValue: 0, unit: '' }])}
            >
              Add custom KPI
            </Button>
          </div>
        )
      }
      case 'simulatorStrip': {
        const simVal = rawVal as {
          quotaId: string
          targets?: Record<string, { currentValue?: number; targetValue?: number }>
          customTargets?: Record<string, { label?: string; currentValue?: number; targetValue?: number }>
        } | null
        if (!simVal || !simVal.quotaId) return null
        return (
          <div className="entityFormDialog__simulator" style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--surface-muted)', padding: 12, borderRadius: 'var(--radius-ctl)' }}>
            <p style={{ margin: 0, fontWeight: 500, fontSize: 13, color: 'var(--muted)' }}>Simulator (Test Mode)</p>
            {Object.entries(simVal.targets ?? {}).map(([key, entry]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13 }}>{key} ({entry.currentValue}/{entry.targetValue})</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onFieldAction?.(field.id, { action: 'bump', target: key, amount: 1 })}
                >
                  +1
                </Button>
              </div>
            ))}
            {Object.entries(simVal.customTargets ?? {}).map(([key, entry]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13 }}>{entry.label} ({entry.currentValue}/{entry.targetValue})</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onFieldAction?.(field.id, { action: 'bumpCustom', target: key, amount: 1 })}
                >
                  +1
                </Button>
              </div>
            ))}
          </div>
        )
      }
      case 'rewardTierRepeater': {
        const tiers = (rawVal as Array<{ percent: number; label: string; badgeUrl?: string | null }>) || []
        return (
          <div className="entityFormDialog__repeater">
            {tiers.map((tier, idx) => (
              <div key={idx} className="entityFormDialog__repeaterRow" style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  placeholder="%"
                  value={tier.percent || ''}
                  onChange={(e) => {
                    const next = [...tiers]
                    next[idx].percent = Number(e.target.value)
                    updateValue(field.id, next)
                  }}
                  style={{ width: 60 }}
                />
                <span>%</span>
                <input
                  type="text"
                  placeholder="Label (e.g. Gold)"
                  value={tier.label}
                  onChange={(e) => {
                    const next = [...tiers]
                    next[idx].label = e.target.value
                    updateValue(field.id, next)
                  }}
                  style={{ flex: 1 }}
                />
                <IconButton
                  variant="tertiary"
                  size="sm"
                  tone="danger"
                  label={getNlsString('NLSCommonEntityFormRemoveTier')}
                  icon={<CloseRoundedIcon fontSize="small" />}
                  onClick={() => updateValue(field.id, tiers.filter((_, i) => i !== idx))}
                />
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              icon={<AddRoundedIcon fontSize="small" />}
              onClick={() => updateValue(field.id, [...tiers, { percent: 0, label: '', badgeUrl: null }])}
            >
              Add reward tier
            </Button>
          </div>
        )
      }

      default:
        return (
          <input
            id={inputId}
            type="text"
            className="entityFormDialog__input"
            placeholder={field.placeholder}
            value={typeof rawVal === 'string' ? rawVal : ''}
            disabled={field.disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : field.hint ? hintId : undefined}
            onChange={(e) => updateValue(field.id, e.target.value)}
          />
        )
    }
  }

  const renderContent = () => (
    <div ref={containerRef} className="entityFormDialog" data-testid="entityFormDialog">
      <div className="entityFormDialog__body">
        {sections.map((section) => (
          <section key={section.id} className="entityFormDialog__section">
            <div className="entityFormDialog__sectionHeader">
              <span className="entityFormDialog__sectionEyebrow">{section.label}</span>
              {section.hint && (
                <p className="entityFormDialog__sectionHint">{section.hint}</p>
              )}
            </div>
            <div className="entityFormDialog__sectionFields">
              {section.fields.map((field) => {
                const error = errors[field.id]
                const inputId = `${formId}-${field.id}`
                const errorId = `${inputId}-error`
                const hintId = `${inputId}-hint`
                return (
                  <div
                    key={field.id}
                    className={`entityFormDialog__field${field.fullWidth || field.type === 'textarea' || field.type === 'image' ? ' entityFormDialog__field--full' : ''}`}
                  >
                    <label htmlFor={inputId} className="entityFormDialog__fieldLabel">
                      <span>{field.label}</span>
                      {field.required && <span className="entityFormDialog__fieldRequired">*</span>}
                    </label>
                    {renderField(field)}
                    {field.hint && !error && (
                      <span id={hintId} className="entityFormDialog__fieldHint">
                        {field.hint}
                      </span>
                    )}
                    {error && (
                      <span id={errorId} className="entityFormDialog__fieldError" role="alert">
                        {error}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        ))}
      </div>

      {showDiscardConfirm && (
        <AppDialog
          open={showDiscardConfirm}
          title="Discard unsaved changes?"
          message="You have unsaved changes. Are you sure you want to discard them?"
          onDismiss={() => setShowDiscardConfirm(false)}
          actions={[
            {
              label: 'Keep editing',
              variant: 'ghost',
              onClick: () => setShowDiscardConfirm(false),
            },
            {
              label: 'Discard',
              variant: 'danger',
              onClick: () => {
                try {
                  sessionStorage.removeItem(storageKey)
                } catch {
                  // ignore
                }
                setShowDiscardConfirm(false)
                setIsDirty(false)
                onClose()
              },
            },
          ]}
        />
      )}

      {showAddSupplierModal && (
        <EntityFormDialog
          open={showAddSupplierModal}
          entityKind="stock_supplier"
          targetId="new"
          entityName="Supplier"
          workspaceId={workspaceId}
          sections={SUPPLIER_FORM_SECTIONS}
          onClose={() => setShowAddSupplierModal(false)}
          onSave={async (supplierValues) => {
            if (!workspaceId) return
            const newId = await createStockSupplier({
              workspaceId,
              name: String(supplierValues.name ?? ''),
              contactEmail: supplierValues.contactEmail ? String(supplierValues.contactEmail) : null,
              contactPhone: supplierValues.contactPhone ? String(supplierValues.contactPhone) : null,
              address: supplierValues.address ? String(supplierValues.address) : null,
              notes: supplierValues.notes ? String(supplierValues.notes) : null,
            })
            if (activeSupplierFieldId) {
              updateValue(activeSupplierFieldId, newId)
            }
            setShowAddSupplierModal(false)
          }}
        />
      )}

      {showAddVendorModal && (
        <EntityFormDialog
          open={showAddVendorModal}
          entityKind="budget_vendor"
          targetId="new"
          entityName="Vendor"
          workspaceId={workspaceId}
          sections={VENDOR_FORM_SECTIONS}
          onClose={() => setShowAddVendorModal(false)}
          onSave={async (vendorValues) => {
            if (!workspaceId) return
            const newId = await createBudgetVendor({
              workspaceId,
              name: String(vendorValues.name ?? ''),
              contactEmail: vendorValues.contactEmail ? String(vendorValues.contactEmail) : null,
              taxId: vendorValues.taxId ? String(vendorValues.taxId) : null,
              notes: vendorValues.notes ? String(vendorValues.notes) : null,
            })
            if (activeVendorFieldId) {
              updateValue(activeVendorFieldId, newId)
            }
            setShowAddVendorModal(false)
          }}
        />
      )}

      {showEntityPickerModal && (
        <AppDialog
          open={true}
          size="attachPicker"
          title="Select target item"
          onDismiss={() => setShowEntityPickerModal(false)}
          actions={[]}
          message={
            <WorkspaceItemPicker
              workspaceId={workspaceId}
              enabledKinds={['timeline']}
              elements={(elements && elements.length > 0) ? elements : timelineElements}
              onSelect={(ref) => {
                const pickerElements = (elements && elements.length > 0) ? elements : timelineElements
                const chain = buildBudgetEntityChain(
                  {
                    id: ref.id,
                    kind: ref.kind,
                    title: ref.label,
                    parentId: ref.parentId,
                  },
                  pickerElements,
                )
                const targetRef = {
                  id: ref.id,
                  titles: chain.map((c) => c.title || ref.label),
                  chain,
                }
                if (activeEntityFieldId) {
                  updateValue(activeEntityFieldId, targetRef)
                }
                setShowEntityPickerModal(false)
              }}
              onClose={() => setShowEntityPickerModal(false)}
            />
          }
        />
      )}
      {showMemberPickerModal && activeMemberFieldId && (
        <AppDialog
          open={true}
          size="attachPicker"
          title="Select member"
          onDismiss={() => setShowMemberPickerModal(false)}
          actions={[]}
          message={
            <WorkspaceItemPicker
              workspaceId={workspaceId}
              enabledKinds={['members']}
              members={members}
              onSelect={(ref) => {
                const section = sections.find(s => s.fields.some(f => f.id === activeMemberFieldId))
                const field = section?.fields.find(f => f.id === activeMemberFieldId)
                const isMulti = field?.options?.[0] === 'multi'
                
                if (isMulti) {
                  const currentList = (values[activeMemberFieldId] as string[]) || []
                  if (!currentList.includes(ref.id)) {
                    updateValue(activeMemberFieldId, [...currentList, ref.id])
                  }
                } else {
                  updateValue(activeMemberFieldId, ref.id)
                  setShowMemberPickerModal(false)
                }
              }}
              onClose={() => setShowMemberPickerModal(false)}
            />
          }
        />
      )}
    </div>
  )


  const actions: AppDialogAction[] = [
    {
      label: getNlsString('NLSCommonActionCancel'),
      variant: 'ghost',
      onClick: handleDismiss,
    },
    ...(!isEditing && onSaveAndAddAnother
      ? [
          {
            label: getNlsString('NLSCommonEntityFormSaveAndAddAnother'),
            variant: 'secondary' as const,
            disabled: hasErrors || submitting,
            loading: submitting,
            onClick: () => handleSaveAndAddAnother(),
          },
        ]
      : []),
    {
      label: submitting
        ? getNlsString('NLSCommonEntityFormSaving')
        : getNlsString('NLSCommonEntityFormSave'),
      variant: 'primary',
      autoFocus: false,
      disabled: hasErrors || submitting,
      loading: submitting,
      onClick: () => handleSave(),
    },
  ]

  return (
    <AppDialog
      open={open}
      size="lg"
      title={entityName}
      headerExtra={
        <span
          className="entityFormDialog__modeChip"
          data-mode={isEditing ? 'edit' : 'new'}
        >
          {isEditing
            ? getNlsString('NLSCommonEntityFormEditingTitle', { title: entityTitle || '' })
            : getNlsString('NLSCommonEntityFormNew')}
        </span>
      }
      onDismiss={handleDismiss}
      actions={actions}
    >
      {renderContent()}
    </AppDialog>
  )
}

